'use client';

import { Toggle as TogglePrimitive } from '@base-ui/react/toggle';
import { ToggleGroup as ToggleGroupPrimitive } from '@base-ui/react/toggle-group';
import * as React from 'react';

import type { ControlSize } from '@playbooks/ui/components/control-size';
import { controlHeightClasses, controlIconClasses } from '@playbooks/ui/components/control-size';
import { cn } from '@playbooks/ui/lib/utils';

import { cva, VariantProps } from 'class-variance-authority';

const ToggleGroupContext = React.createContext<{ size: ControlSize }>({ size: 'default' });
const toggleGroupVariants = cva(
	'inline-flex min-w-0 items-center gap-1 rounded-panel border border-border bg-canvas p-1',
	{
		variants: {
			size: controlHeightClasses,
		},
		defaultVariants: {
			size: 'default',
		},
	},
);
const toggleGroupItemVariants = cva(
	'inline-flex h-full min-w-0 flex-1 items-center justify-center gap-1.5 rounded-panel text-sm font-medium text-foreground-body transition-colors outline-none hover:bg-surface-hover hover:text-foreground focus-visible:ring-3 focus-visible:ring-focus/50 data-pressed:bg-surface-hover data-pressed:text-foreground disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
	{
		variants: {
			size: {
				xs: `px-1.5 ${controlIconClasses.xs}`,
				sm: `px-2 ${controlIconClasses.sm}`,
				default: `px-3 ${controlIconClasses.default}`,
				lg: `px-3.5 ${controlIconClasses.lg}`,
			},
		},
		defaultVariants: {
			size: 'default',
		},
	},
);

type ToggleGroupProps = ToggleGroupPrimitive.Props & VariantProps<typeof toggleGroupVariants>;
function ToggleGroup({ className, size: sizeProp, ...props }: ToggleGroupProps) {
	const size = sizeProp || 'default';

	// Theme
	const rootClassName = cn(toggleGroupVariants({ size }), className);

	// Render
	return (
		<ToggleGroupContext.Provider value={{ size }}>
			<ToggleGroupPrimitive data-slot='toggle-group' data-size={size} className={rootClassName} {...props} />
		</ToggleGroupContext.Provider>
	);
}

type ToggleGroupItemProps = TogglePrimitive.Props & VariantProps<typeof toggleGroupItemVariants>;
function ToggleGroupItem({ className, size: sizeProp, ...props }: ToggleGroupItemProps) {
	const { size: contextSize } = React.useContext(ToggleGroupContext);
	const size = sizeProp || contextSize;

	// Theme
	const rootClassName = cn(toggleGroupItemVariants({ size }), className);

	// Render
	return <TogglePrimitive data-slot='toggle-group-item' data-size={size} className={rootClassName} {...props} />;
}

export { ToggleGroup, ToggleGroupItem, toggleGroupItemVariants, toggleGroupVariants };
export type { ToggleGroupItemProps, ToggleGroupProps };
